import { apiRequest } from "../auth/api";

export interface TaxonomyAliasView {
  id: string;
  value: string;
  normalized: string;
  locale: string;
  sortOrder: number;
}

export interface TaxonomyItemView {
  id: string;
  namespace: string;
  code: string;
  slug: string;
  nameVi: string;
  nameEn: string | null;
  parentId: string | null;
  status: "active" | "deprecated" | "archived";
  sortOrder: number;
  metadata: Record<string, unknown>;
  sourceNote: string | null;
  replacementItemId: string | null;
  seeded: boolean;
  version: number;
  aliases: TaxonomyAliasView[];
  updatedAt: string;
}

export interface TaxonomyNamespaceView {
  code: string;
  nameVi: string;
  nameEn: string | null;
  descriptionVi: string | null;
  status: "active" | "deprecated" | "archived";
  sortOrder: number;
  supportsHierarchy: boolean;
  sourceNote: string | null;
  version: number;
}

export function getAdminTaxonomyNamespaces() {
  return apiRequest<{ namespaces: TaxonomyNamespaceView[]; requestId: string }>(
    "/api/v1/admin/taxonomy/namespaces",
  );
}

export function getAdminTaxonomyItems(input: {
  namespace: string;
  query?: string;
  status?: string;
  cursor?: string | null;
  limit?: number;
}) {
  const params = new URLSearchParams({ namespace: input.namespace });
  if (input.query) params.set("q", input.query);
  if (input.status) params.set("status", input.status);
  if (input.cursor) params.set("cursor", input.cursor);
  if (input.limit) params.set("limit", String(input.limit));
  return apiRequest<{
    items: TaxonomyItemView[];
    nextCursor: string | null;
    total: number;
    requestId: string;
  }>(`/api/v1/admin/taxonomy/items?${params.toString()}`);
}

export function getAdminTaxonomyItem(id: string) {
  return apiRequest<{ item: TaxonomyItemView; references: number; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(id)}`,
  );
}

export function createAdminTaxonomyItem(body: {
  namespace: string;
  code: string;
  slug: string;
  nameVi: string;
  nameEn: string | null;
  parentId: string | null;
  status: string;
  sortOrder: number;
  metadata: Record<string, unknown>;
  sourceNote: string | null;
}) {
  return apiRequest<{ item: TaxonomyItemView; requestId: string }>(
    "/api/v1/admin/taxonomy/items",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function patchAdminTaxonomyItem(
  id: string,
  body: Partial<{
    code: string;
    slug: string;
    nameVi: string;
    nameEn: string | null;
    parentId: string | null;
    status: string;
    sortOrder: number;
    metadata: Record<string, unknown>;
    sourceNote: string | null;
  }> & { expectedVersion: number },
) {
  return apiRequest<{ item: TaxonomyItemView; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function addAdminTaxonomyAlias(
  itemId: string,
  body: { alias: string; locale: string; sortOrder?: number },
) {
  return apiRequest<{ item: TaxonomyItemView; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(itemId)}/aliases`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function removeAdminTaxonomyAlias(itemId: string, aliasId: string) {
  return apiRequest<{ item: TaxonomyItemView; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(itemId)}/aliases/${encodeURIComponent(aliasId)}`,
    { method: "DELETE" },
  );
}

export function bulkAdminTaxonomyStatus(ids: string[], status: string) {
  return apiRequest<{ updated: number; requestId: string }>(
    "/api/v1/admin/taxonomy/bulk-status",
    { method: "POST", body: JSON.stringify({ ids, status }) },
  );
}

export function reorderAdminTaxonomy(
  entries: Array<{ id: string; sortOrder: number; expectedVersion: number }>,
) {
  return apiRequest<{ updated: number; requestId: string }>(
    "/api/v1/admin/taxonomy/reorder",
    { method: "POST", body: JSON.stringify({ entries }) },
  );
}

export function deprecateAdminTaxonomyItem(
  id: string,
  body: { replacementItemId: string; reason: string; expectedVersion: number },
) {
  return apiRequest<{ item: TaxonomyItemView; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(id)}/deprecate`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function deleteAdminTaxonomyItem(id: string) {
  return apiRequest<{ deleted: true; requestId: string }>(
    `/api/v1/admin/taxonomy/items/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}
